import { inject, Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  map,
  mergeMap,
  switchMap,
} from 'rxjs/operators';
import * as userActions from './user.actions';
import { AuthService } from '../../services/auth.service';
import { EmptyAction } from '..';
import { SnackBarService } from '../../services/snackbar.service';
import { UserService } from '../../services/user.service';
import { Store } from '@ngrx/store';
import { LoginSuccessFulAction, LogoutAction } from '../auth/auth.actions';
import { IRefreshTokenRequestBody, IUserInfo } from '../../models/user';
import { Router } from '@angular/router';
import { CorePaths } from '../../modules/core/core-routing.module';
import { ContentManagementService } from '../../services/contentManagement.services';
import { ParentalGateSessionService } from '../../services/parentalGateSession.service';

@Injectable()
export class UserEffects {
  // Inject Services and Actions
  private actions$ = inject(Actions);
  private authService = inject(AuthService);
  private snackBarService = inject(SnackBarService);
  private userService = inject(UserService);
  private contentManagementService = inject(ContentManagementService);
  private store = inject(Store);
  private router = inject(Router);
  private parentalGateSession = inject(ParentalGateSessionService);

  // Generate Payment Order
  generatePaymentOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.GeneratePaymentOrderAction),
      switchMap((props) => {
        return this.userService.getPaymentOrder(props.orderId).pipe(
          map((res) => {
            return userActions.GeneratePaymentOrderSuccessAction({
              response: {
                Errors: [],
                Value: res.Value,
              },
            });
          }),
          catchError((err) => {
            return of(
              userActions.GeneratePaymentOrderFailureAction({
                error: err.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  // Generate Payment Order Success
  generatePaymentOrderSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.GeneratePaymentOrderSuccessAction),
      switchMap((props) => {
        return of(EmptyAction());
      }),
    ),
  );

  // Generate Payment Order Failure
  generatePaymentOrderFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.GeneratePaymentOrderFailureAction),
      switchMap((props) => {
        this.snackBarService.showSnackBar(
          'error',
          props?.error?.Errors?.[0]?.Message ||
            'Unable to Generate the Payment Order.',
        );
        return of(EmptyAction());
      }),
    ),
  );

  // Get Order
  getOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.GetOrderAction),
      switchMap((props) => {
        return this.userService.getOrder(props.orderId).pipe(
          map((res) => {
            return userActions.GetOrderSuccessAction({
              response: {
                Errors: [],
                Value: res.Value,
              },
            });
          }),
          catchError((err) => {
            return of(
              userActions.GetOrderFailureAction({
                error: err.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  // Get Order Success
  getOrderSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.GetOrderSuccessAction),
      switchMap((props) => {
        // Clear the Orders State When User Updates A Order Information
        this.store.dispatch(userActions.ClearOrdersStateAction());
        return of(EmptyAction());
      }),
    ),
  );

  // Get Order Failure
  getOrderFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.GetOrderFailureAction),
      switchMap((props) => {
        // this.snackBarService.showSnackBar(
        //   'error',
        //   props?.error?.Errors?.[0]?.Message ||
        //     'Fetching Order Details Failed.',
        // );
        return of(EmptyAction());
      }),
    ),
  );

  // Get All Orders
  getAllOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.FetchOrdersAction),
      switchMap((props) => {
        return this.userService
          .getAllOrders(
            props.request.PageNumber,
            props.request.PageSize,
            props.request.OrderStatus,
            props.request.PaymentStatus,
          )
          .pipe(
            map((res) => {
              return userActions.FetchOrdersSuccessAction({
                response: res,
                paymentStatus: props.request.PaymentStatus,
                orderStatus: props.request.OrderStatus,
              });
            }),
            catchError((err) => {
              return of(
                userActions.FetchOrdersFailureAction({
                  error: err.error,
                }),
              );
            }),
          );
      }),
    ),
  );

  // Get All Orders Success
  getAllOrdersSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.FetchOrdersSuccessAction),
      switchMap((props) => {
        return of(EmptyAction());
      }),
    ),
  );

  // Get All Orders Failure
  getAllOrdersFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.FetchOrdersFailureAction),
      switchMap((props) => {
        this.snackBarService.showSnackBar(
          'error',
          props?.error?.Errors?.[0]?.Message || 'Fetching Orders Failed.',
        );
        return of(EmptyAction());
      }),
    ),
  );

  // Create Order
  createOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.CreateOrderAction),
      switchMap((props) => {
        return this.userService.createOrder(props.request).pipe(
          map((res) => {
            return userActions.CreateOrderSuccessAction({
              response: {
                Value: res.Value,
                Errors: [],
              },
            });
          }),
          catchError((err) => {
            return of(
              userActions.CreateOrderFailureAction({
                error: err.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  // Create Order Success
  createOrderSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.CreateOrderSuccessAction),
      switchMap((props) => {
        // When User Creates A Order, Clear the Orders State
        this.store.dispatch(userActions.ClearOrdersStateAction());
        return of(EmptyAction());
      }),
    ),
  );

  // Create Order Failure
  createOrderFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.CreateOrderFailureAction),
      switchMap((props) => {
        this.snackBarService.showSnackBar(
          'error',
          props?.error?.Errors?.[0]?.Message || 'Creating Order Failed',
        );
        return of(EmptyAction());
      }),
    ),
  );

  // Create Auto Renew Order
  createAutoRenewOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.CreateAutoRenewOrderAction),
      switchMap((props) => {
        return this.userService
          .createAutoRenewOrder(props.request, props.totalCycles)
          .pipe(
            map((res) => {
              return userActions.CreateOrderSuccessAction({
                response: {
                  Value: res.Value,
                  Errors: [],
                },
              });
            }),
            catchError((err) => {
              return of(
                userActions.CreateOrderFailureAction({
                  error: err.error,
                }),
              );
            }),
          );
      }),
    ),
  );

  // Cancel Auto Renewing Subscription
  cancelAutoRenewingSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.CancelAutoRenewingSubscriptionAction),
      exhaustMap((props) => {
        return this.userService
          .cancelAutoRenewingSubscription(props.request)
          .pipe(
            map((res) => {
              if (res.Value === true) {
                return userActions.CancelAutoRenewingSubscriptionSuccessAction({
                  response: res,
                });
              }
              return userActions.CancelAutoRenewingSubscriptionFailureAction({
                error: { Errors: [] },
              });
            }),
            catchError((err) => {
              return of(
                userActions.CancelAutoRenewingSubscriptionFailureAction({
                  error: err.error ?? { Errors: [] },
                }),
              );
            }),
          );
      }),
    ),
  );

  // Cancel Auto Renewing Subscription Success - refresh the plans so the card
  // shows the cancelled auto-renewal instead of the Cancel button
  cancelAutoRenewingSubscriptionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.CancelAutoRenewingSubscriptionSuccessAction),
      map(() => userActions.FetchSubscriptionPlansAction()),
    ),
  );

  // Update Order
  updateOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.UpdateOrderAction),
      switchMap((props) => {
        return this.userService.updateOrder(props.request).pipe(
          map((res) => {
            return userActions.UpdateOrderSuccessAction({
              response: {
                Value: res.Value,
                Errors: [],
              },
            });
          }),
          catchError((err) => {
            return of(
              userActions.UpdateOrderFailureAction({
                error: err.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  // Update Order Success
  updateOrderSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.UpdateOrderSuccessAction),
      switchMap((props) => {
        // When User Updates A Order, Clear the Orders State
        this.store.dispatch(userActions.ClearOrdersStateAction());
        return of(EmptyAction());
      }),
    ),
  );

  // Update Order Failure
  updateOrderFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.UpdateOrderFailureAction),
      switchMap((props) => {
        this.snackBarService.showSnackBar(
          'error',
          props?.error?.Errors?.[0]?.Message || 'Order updation Failed',
        );
        return of(EmptyAction());
      }),
    ),
  );

  // Fetch Subscription Plans
  fetchSubscriptionPlans$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.FetchSubscriptionPlansAction),
      switchMap((props) => {
        return this.userService.getSubscriptionPlans().pipe(
          map((res) => {
            return userActions.FetchSubscriptionPlansSuccessAction({
              response: {
                Value: res.Value,
                Errors: [],
              },
            });
          }),
          catchError((err) => {
            return of(
              userActions.FetchSubscriptionPlansFailureAction({
                error: err.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  // Fetch Subscription Plans Success
  fetchSubscriptionPlansSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.FetchSubscriptionPlansSuccessAction),
      switchMap((props) => {
        return of(EmptyAction());
      }),
    ),
  );

  // Fetch Subscription Plans Failure
  fetchSubscriptionPlansFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.FetchSubscriptionPlansFailureAction),
      switchMap((props) => {
        this.snackBarService.showSnackBar(
          'error',
          props?.error?.Errors?.[0]?.Message ||
            'Failed to load Subscription Plans',
        );
        return of(EmptyAction());
      }),
    ),
  );

  //Authorize User
  authorizeUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.AuthorizeUserAction),
      switchMap((props) => {
        return this.userService
          .authorize(this.authService.getSavedTempToken()!)
          .pipe(
            map((res) => {
              return userActions.AuthorizeUserSuccessAction({
                response: {
                  Value: res.Value,
                },
              });
            }),
            catchError((err) => {
              return of(
                userActions.AuthorizeUserFailureAction({
                  error: err.error,
                }),
              );
            }),
          );
      }),
    ),
  );

  // Authorize User Success
  authorizeUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.AuthorizeUserSuccessAction),
      map((props) => {
        // Start parental gate session after login, if needed
        setTimeout(() => {
          const tokenInfo = this.authService.getDecodedToken();
          if (tokenInfo && tokenInfo?.['ParentalToken'] === 'True') {
            this.parentalGateSession.startSession();
          }
        }, 1000);

        const expiryTime =
          new Date().getTime() + props.response.Value.ExpiresIn * 1000;
        return LoginSuccessFulAction({
          loginSuccessInfo: {
            AccessToken: props.response.Value.AccessToken,
            RefreshToken: props.response.Value.RefreshToken,
            Expiry: expiryTime.toString(),
          },
        });
      }),
    ),
  );

  // Authorize User Failure
  authorizeUserFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.AuthorizeUserFailureAction),
      switchMap((props) => {
        return of(EmptyAction());
      }),
    ),
  );

  // Get User
  getUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.GetUserAction),
      switchMap((props) => {
        return this.userService
          .getUser(
            props.request.routeParameters.userId,
            props.request.queryParameters.includeRoles,
          )
          .pipe(
            map((res) => {
              this.userService.saveUserInfo(res.Value);
              return userActions.GetUserSuccessAction({
                response: {
                  Value: res.Value,
                },
              });
            }),
            catchError((err) => {
              return of(
                userActions.GetUserFailureAction({
                  error: err.error,
                }),
              );
            }),
          );
      }),
    ),
  );

  // Get User Success
  getUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.GetUserSuccessAction),
      switchMap((props) => {
        // this.userService.saveUserInfo(props.response.Value);
        return of(EmptyAction());
      }),
    ),
  );

  // Get User Failure
  getUserFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.GetUserFailureAction),
      switchMap((props) => {
        console.error('Getting User Failed');
        return of(EmptyAction());
      }),
    ),
  );

  // Update User
  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.UpdateUserAction),
      switchMap((props) => {
        return this.userService.updateUser(props.userId, props.request).pipe(
          map((res) => {
            let userInfo: IUserInfo = {
              UserId: res.Value.UserId ?? null,
              Fullname: res.Value.Fullname ?? null,
              Email: res.Value.Email ?? null,
              Mobile: res.Value.Mobile ?? null,
              Gender: res.Value.Gender ?? null,
              Dob: res.Value.Dob ?? null,
              Grade: res.Value.Grade ?? null,
              MotherTongue: res.Value.MotherTongue ?? null,
              PreferredLanguage: res.Value.PreferredLanguage ?? null,
              GuardianName: res.Value.GuardianName ?? null,
              Address: res.Value.Address ?? null,
              PrimaryAddress: res.Value.PrimaryAddress ?? null,
              PictureUrl: res.Value.PictureUrl ?? null,
              PrimarySubscription: null,
              Declarations: res.Value.Declarations ?? null,
              Roles: res.Value.Roles ?? [],
              Subscriptions: res.Value.Subscriptions ?? [],
            };
            this.userService.saveUserInfo(userInfo);
            return userActions.UpdateUserSuccessAction({
              response: {
                Value: res.Value,
              },
            });
          }),
          catchError((err) => {
            return of(
              userActions.UpdateUserFailureAction({
                error: err.error,
                initiatedAfterMobileDelete: props.initiatedAfterMobileDelete,
              }),
            );
          }),
        );
      }),
    ),
  );

  // Update User Success
  updateUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.UpdateUserSuccessAction),
      switchMap((props) => {
        const currentUrl = this.router.url;
        if (
          currentUrl.includes(CorePaths.profile) ||
          currentUrl.includes(CorePaths.order)
        ) {
          // Refresh The Token After Updating Profile
          this.store.dispatch(
            userActions.RefreshTokenAction({
              request: {
                Token: this.authService.getTokenInformation()?.RefreshToken!,
              },
            }),
          );
          setTimeout(() => {
            this.snackBarService.showSnackBar(
              'success',
              'Profile updated successfully!',
            );
          }, 1000);
          return of(EmptyAction());
        }
        this.authService.autoRedirect();
        return of(EmptyAction());
      }),
    ),
  );

  // Update User Failure
  updateUserFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.UpdateUserFailureAction),
      switchMap((props) => {
        if (props.initiatedAfterMobileDelete) {
          this.snackBarService.showSnackBar(
            'error',
            'Mobile number removed successfully, but updating user failed',
          );
        } else {
          this.snackBarService.showSnackBar('error', 'Updating User Failed');
        }
        return of(EmptyAction());
      }),
    ),
  );

  // Delete User Profile
  DeleteUserAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.DeleteUserAccountAction),
      switchMap((props) => {
        return this.userService.deleteUserAccount(props.userId).pipe(
          map((res) => {
            return userActions.DeleteUserAccountSuccessAction({
              response: {
                Value: res.Value,
              },
            });
          }),
          catchError((err) => {
            return of(
              userActions.DeleteUserAccountFailureAction({
                error: err.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  // Delete User Profile Success
  DeleteUserAccountSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.DeleteUserAccountSuccessAction),
      switchMap((props) => {
        const currentUrl = this.router.url;
        if (currentUrl.includes(CorePaths.profile)) {
          this.snackBarService.showSnackBar(
            'success',
            'Account deleted successfully!',
          );

          // Logout after Profile Delete
          this.store.dispatch(LogoutAction({}));
          return of(EmptyAction());
        }
        this.authService.autoRedirect();
        return of(EmptyAction());
      }),
    ),
  );

  // Update User Failure
  DeleteUserAccountFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.DeleteUserAccountFailureAction),
      switchMap((props) => {
        console.error('Failed to delete User Account, Please contact support');
        return of(EmptyAction());
      }),
    ),
  );

  // Check If The User Exists Or Not With Mobile/Email
  CheckUserPhoneOrEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.CheckUserPhoneOrEmailAction),
      switchMap((props) => {
        return this.userService
          .checkUserExistsByMobileOrEmail(props.mobileOrEmail)
          .pipe(
            map((res) => {
              return userActions.CheckUserPhoneOrEmailSuccessAction({
                response: {
                  Value: res.Value,
                  Errors: [],
                },
                field: props.field,
              });
            }),
            catchError((err) => {
              return of(
                userActions.CheckUserPhoneOrEmailFailureAction({
                  error: err.error,
                }),
              );
            }),
          );
      }),
    ),
  );

  // Delete user mobile number
  deleteUserMobile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.DeleteUserMobileAction),
      switchMap(() => {
        return this.userService.deleteUserMobile().pipe(
          map((res) => {
            return userActions.DeleteUserMobileSuccessAction({
              response: {
                Value: res.Value,
                Errors: [],
              },
            });
          }),
          catchError((err) => {
            return of(
              userActions.DeleteUserMobileFailureAction({
                error: err.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  // Delete User mobile number Success
  deleteUserMobileSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.DeleteUserMobileSuccessAction),
      switchMap((props) => {
        if (props.response.Value) {
          // Refresh The Token After Updating Profile
          this.store.dispatch(
            userActions.RefreshTokenAction({
              request: {
                Token: this.authService.getTokenInformation()?.RefreshToken!,
              },
            }),
          );
          this.snackBarService.showSnackBar(
            'success',
            'Profile updated successfully!',
          );
        } else {
          this.snackBarService.showSnackBar(
            'error',
            'Mobile number updation failed!',
          );
        }
        return of(EmptyAction());
      }),
    ),
  );

  // Delete User mobile number Failure
  deleteUserMobileFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.DeleteUserMobileFailureAction),
      switchMap((props) => {
        this.snackBarService.showSnackBar(
          'error',
          'Mobile number updation failed!',
        );
        return of(EmptyAction());
      }),
    ),
  );

  // Delete user mobile number and update user
  deleteMobileAndUpdateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.DeleteMobileAndUpdateUserAction),
      switchMap((userData) => {
        return this.userService.deleteUserMobile().pipe(
          mergeMap((res) => [
            userActions.DeleteMobileAndUpdateUserSuccessAction({
              response: {
                Value: res.Value,
                Errors: [],
              },
            }),
            userActions.UpdateUserAction({
              ...userData,
              initiatedAfterMobileDelete: true,
            }),
          ]),
          catchError((err) => {
            return of(
              userActions.DeleteMobileAndUpdateUserFailureAction({
                error: err.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  // Delete User mobile number Failure
  deleteMobileAndUpdateUserFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.DeleteMobileAndUpdateUserFailureAction),
      switchMap((props) => {
        this.snackBarService.showSnackBar(
          'error',
          'Mobile number updation failed!',
        );
        return of(EmptyAction());
      }),
    ),
  );

  //  Check If The User Exists Or Not With Mobile/Email - Success
  CheckUserPhoneOrEmailSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.CheckUserPhoneOrEmailSuccessAction),
      switchMap((props) => {
        return of(EmptyAction());
      }),
    ),
  );

  //  Check If The User Exists Or Not With Mobile/Email - Failure
  CheckUserPhoneOrEmailFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.CheckUserPhoneOrEmailFailureAction),
      switchMap((props) => {
        this.snackBarService.showSnackBar(
          'error',
          props.error?.Errors[0]?.Message ||
            'Error Validating User Phone Or Email.',
        );
        return of(EmptyAction());
      }),
    ),
  );

  //Refresh Token
  refreshToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.RefreshTokenAction),
      switchMap(({ request }) => {
        const refreshTokenData: IRefreshTokenRequestBody = {
          Token: this.authService.getTokenInformation()?.RefreshToken!,
          OtpUserInput: request.OtpUserInput,
          OtpVerificationToken: request.OtpVerificationToken,
          RefreshParentalControl: request.RefreshParentalControl ?? true,
        };
        return this.userService.refreshToken(refreshTokenData).pipe(
          map((res) => {
            return userActions.RefreshTokenSuccessAction({
              response: {
                Value: res.Value,
              },
            });
          }),
          catchError((err) => {
            return of(
              userActions.RefreshTokenFailureAction({
                error: err.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  // Refresh Token Success
  refreshTokenSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.RefreshTokenSuccessAction),
      map((props) => {
        return LoginSuccessFulAction({
          loginSuccessInfo: {
            AccessToken: props.response.Value.AccessToken,
            RefreshToken: props.response.Value.RefreshToken,
            Expiry: (
              new Date().getTime() +
              props.response.Value.ExpiresIn * 1000
            ).toString(),
          },
        });
      }),
    ),
  );

  // Refresh Token Failure
  refreshTokenFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.RefreshTokenFailureAction),
      switchMap((props) => {
        const currentRoute = this.router.url;
        if (currentRoute.includes(CorePaths.parentalGate)) {
          this.snackBarService.showSnackBar(
            'error',
            props.error.Errors[0].Message,
          );
          return of(EmptyAction());
        }
        this.store.dispatch(LogoutAction({}));
        return of(EmptyAction());
      }),
    ),
  );

  // Fetch User Books
  fetchUserBooks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.FetchUserBooksAction),
      switchMap((props) => {
        return this.contentManagementService
          .getBooks(props.pageNumber, props.pageSize)
          .pipe(
            map((res) => {
              return userActions.FetchUserBooksSuccessAction({
                response: res,
                pageNumber: props.pageNumber,
                pageSize: props.pageSize,
              });
            }),
            catchError((err) => {
              return of(
                userActions.FetchUserBooksFailureAction({
                  error: err.error,
                }),
              );
            }),
          );
      }),
    ),
  );

  // Fetch User Books Failure
  fetchUserBooksSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.FetchUserBooksSuccessAction),
      switchMap((props) => {
        return of(EmptyAction());
      }),
    ),
  );

  // Fetch User Books Failure
  fetchUserBooksFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.FetchUserBooksFailureAction),
      switchMap((props) => {
        console.error('Fetching User Books Failed');
        return of(EmptyAction());
      }),
    ),
  );

  // Fetch Book With Chapter
  fetchBookWithChapter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.FetchBookWithChapterAction),
      switchMap((props) => {
        return this.contentManagementService.getBook(props.bookId).pipe(
          map((res) => {
            return userActions.FetchBookWithChapterSuccessAction({
              response: res,
            });
          }),
          catchError((err) => {
            return of(
              userActions.FetchUserBooksFailureAction({
                error: err.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  // Fetch Book With Chapter Success
  fetchBookWithChapterSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.FetchBookWithChapterSuccessAction),
      switchMap((props) => {
        return of(EmptyAction());
      }),
    ),
  );

  // Fetch Book With Chapter Failure
  fetchBookWithChapterFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.FetchBookWithChapterFailureAction),
      switchMap((props) => {
        console.error('Fetching Book With Chapter Failure');
        return of(EmptyAction());
      }),
    ),
  );

  // Fetch Chapter With Pages
  fetchChapterWithPages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.FetchChapterWithPagesAction),
      switchMap((props) => {
        const useTracking = props.useTracking ?? true;
        return this.contentManagementService
          .getPages(
            props.chapterId,
            props.layoutType,
            props.currentPageId,
            props.count,
            useTracking,
            props.languageCode,
          )
          .pipe(
            map((res) => {
              return userActions.FetchChapterWithPagesSuccessAction({
                response: res,
              });
            }),
            catchError((err) => {
              return of(
                userActions.FetchChapterWithPagesFailureAction({
                  error: err.error,
                }),
              );
            }),
          );
      }),
    ),
  );

  // Fetch Chapter With Pages Success
  fetchChapterWithPagesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.FetchChapterWithPagesSuccessAction),
      switchMap((props) => {
        return of(EmptyAction());
      }),
    ),
  );

  // Fetch Book With Chapter Failure
  fetchChapterWithPagesFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.FetchChapterWithPagesFailureAction),
      switchMap((props) => {
        console.error('Fetching Chapter With Pages Failure');
        console.error(props);
        return of(EmptyAction());
      }),
    ),
  );

  // Send Invoice
  sendInvoice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.SendInvoiceAction),
      switchMap((props) => {
        return this.userService.sendInvoice(props.request.OrderId).pipe(
          map((res) => {
            return userActions.SendInvoiceSuccessAction({
              response: res,
            });
          }),
          catchError((err) => {
            return of(
              userActions.SendInvoiceFailureAction({
                error: err.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  // Send Invoice Success
  sendInvoiceSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.SendInvoiceSuccessAction),
      switchMap((props) => {
        this.snackBarService.showSnackBar(
          'success',
          props.response.Value?.EmailResponse || 'Emil Sent Successfully',
        );
        return of(EmptyAction());
      }),
    ),
  );

  // Send Invoice Failure
  sendInvoiceFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.SendInvoiceFailureAction),
      switchMap((props) => {
        this.snackBarService.showSnackBar(
          'error',
          props.error?.Errors[0]?.Message || 'Sending Invoice Failed',
        );
        return of(EmptyAction());
      }),
    ),
  );

  // Get Chapter First Page
  getChapterFirstPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.GetChapterFirstPageAction),
      switchMap((props) => {
        return this.contentManagementService
          .getPages(props.chapterId, 'Web', '', 1, false, props.languageCode)
          .pipe(
            map((res) => {
              return userActions.GetChapterFirstPageSuccessAction({
                response: res,
              });
            }),
            catchError((err) => {
              return of(EmptyAction());
            }),
          );
      }),
    ),
  );

  // Cancel Order
  cancelOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.CancelOrderAction),
      switchMap((props) => {
        return this.userService.cancelOrder(props.request.OrderId).pipe(
          map((res) => {
            if (res.Value === true) {
              return userActions.CancelOrderSuccessAction({ response: res });
            } else {
              return userActions.CancelOrderFailureAction({
                error: {
                  Errors: [],
                },
              });
            }
          }),
          catchError((err) => {
            return of(
              userActions.CancelOrderFailureAction({
                error: err.message || 'Cancelling Order Failed.',
              }),
            );
          }),
        );
      }),
    ),
  );

  // Cancel Order Success
  cancelOrderSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.CancelOrderSuccessAction),
      switchMap((props) => {
        this.snackBarService.showSnackBar(
          'success',
          'Order Cancelled Successfully.',
        );
        return of(EmptyAction());
      }),
    ),
  );

  // Cancel Order Failure
  cancelOrderFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.CancelOrderFailureAction),
      switchMap((props) => {
        this.snackBarService.showSnackBar(
          'error',
          props.error?.Errors[0]?.Message || 'Cancelling Order Failed.',
        );
        return of(EmptyAction());
      }),
    ),
  );

  // Session Remove Devices
  sessionRemoveDevices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.SessionRemoveDevicesAction),
      switchMap((props) => {
        return this.userService.sessionRemoveDevices(props.request).pipe(
          map((res) => {
            if (res.Value) {
              return userActions.SessionRemoveDevicesSuccessAction({
                response: res,
              });
            } else {
              return userActions.SessionRemoveDevicesFailureAction({
                error: {
                  Errors: [],
                },
              });
            }
          }),
          catchError((err) => {
            return of(
              userActions.SessionRemoveDevicesFailureAction({
                error: err.message || 'Removing Devices Failed.',
              }),
            );
          }),
        );
      }),
    ),
  );

  // Session Revoke
  sessionRevoke$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.SessionRevokeAction),
      switchMap((props) => {
        return this.userService.sessionRevoke(props.request).pipe(
          map((res) => {
            if (res.Value) {
              return userActions.SessionRevokeSuccessAction({ response: res });
            } else {
              return userActions.SessionRevokeFailureAction({
                error: {
                  Errors: [],
                },
              });
            }
          }),
          catchError((err) => {
            return of(
              userActions.SessionRevokeFailureAction({
                error: err.message || 'Revoking Sessions Failed.',
              }),
            );
          }),
        );
      }),
    ),
  );

  // Session Refreshed
  sessionRefresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.SessionRefreshAction),
      switchMap((props) => {
        return this.userService.sessionRefresh(props.request).pipe(
          map((res) => {
            if (res.Value) {
              return userActions.SessionRefreshSuccessAction({ response: res });
            } else {
              return userActions.SessionRefreshFailureAction({
                error: {
                  Errors: [],
                },
              });
            }
          }),
          catchError((err) => {
            return of(
              userActions.SessionRefreshFailureAction({
                error: err.message || 'Refreshing Session Failed',
              }),
            );
          }),
        );
      }),
    ),
  );

  // Session Logout
  sessionLogout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.SessionLogoutAction),
      switchMap((props) => {
        return this.userService.sessionLogout().pipe(
          map((res) => {
            if (res.Value) {
              return userActions.SessionLogoutSuccessAction({ response: res });
            } else {
              return userActions.SessionLogoutFailureAction({
                error: {
                  Errors: [],
                },
              });
            }
          }),
          catchError((err) => {
            return of(
              userActions.SessionLogoutFailureAction({
                error: err.message || 'Session Logout Failed.',
              }),
            );
          }),
        );
      }),
    ),
  );

  // Session Logout Success
  sessionLogoutSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.SessionLogoutSuccessAction),
      switchMap((props) => {
        return of(EmptyAction());
      }),
    ),
  );

  // Session Logout Failure
  sessionLogoutFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.SessionLogoutFailureAction),
      switchMap((props) => {
        return of(EmptyAction());
      }),
    ),
  );

  // FulfillOrder
  fulfillOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.FulFillOrderAction),
      switchMap((props) => {
        return this.userService.fulfillOrder(props.request).pipe(
          map((res) => {
            return userActions.FulfillOrderSuccessAction({
              response: res,
            });
          }),
          catchError((err) => {
            return of(
              userActions.FulfillOrderFailureAction({
                error: err.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  // FulfillOrder Success
  fulfillOrderSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.FulfillOrderSuccessAction),
      switchMap((props) => {
        return of(EmptyAction());
      }),
    ),
  );

  // FulfillOrder Failure
  fulfillOrderFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.FulfillOrderFailureAction),
      switchMap((props) => {
        return of(EmptyAction());
      }),
    ),
  );

  // Update Subscription
  updateSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.UpdateSubscriptionAction),
      switchMap((props) => {
        return this.userService
          .subscriptionUpdate(props.subscriptionId, props.request)
          .pipe(
            map((res) => {
              if (res.Value === true) {
                return userActions.UpdateSubscriptionSuccessAction({
                  response: res,
                });
              } else {
                return userActions.UpdateSubscriptionFailureAction({
                  error: {
                    Errors: [],
                  },
                });
              }
            }),
            catchError((err) => {
              return of(
                userActions.UpdateSubscriptionFailureAction({
                  error: err.message || 'Updating Subscription Failed.',
                }),
              );
            }),
          );
      }),
    ),
  );

  // Update Subscription Success
  updateSubscriptionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.UpdateSubscriptionSuccessAction),
      switchMap((props) => {
        this.snackBarService.showSnackBar(
          'success',
          'Subscription Updated Successfully.',
        );
        return of(EmptyAction());
      }),
    ),
  );

  // Update Subscription Failure
  updateSubscriptionFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.UpdateSubscriptionFailureAction),
      switchMap((props) => {
        this.snackBarService.showSnackBar(
          'error',
          props.error?.Errors[0]?.Message || 'Updating Subscription Failed.',
        );
        return of(EmptyAction());
      }),
    ),
  );

  // Get Settings
  GetSetting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.GetSettingAction),
      switchMap((props) => {
        return this.userService.getSettings().pipe(
          map((res) => {
            return userActions.GetSettingSuccessAction({
              response: {
                Value: res.Value,
                Errors: [],
              },
            });
          }),
          catchError((err) => {
            return of(
              userActions.GetSettingFailureAction({
                error: err.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  // Get Setting Success
  GetSettingSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.GetSettingSuccessAction),
      switchMap((props) => {
        // Don't perform any further actions
        return of(EmptyAction());
      }),
    ),
  );

  // Get Setting Failure
  GetSettingFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.GetSettingFailureAction),
      switchMap((props) => {
        // Don't perform any further actions
        return of(EmptyAction());
      }),
    ),
  );

  // Fetch User Reports
  FetchUserReports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.FetchUserReportsAction),
      switchMap((props) => {
        return this.userService.getUserReports(props.request).pipe(
          map((res) => {
            return userActions.FetchUserReportsSuccessAction({
              response: {
                Value: res.Value,
                Errors: [],
                PageNumber: res.PageNumber,
                PageSize: res.PageSize,
                RecordsInPage: res.RecordsInPage,
                TotalPages: res.TotalPages,
                TotalCount: res.TotalCount,
              },
            });
          }),
          catchError((err) => {
            return of(
              userActions.FetchUserReportsFailureAction({
                error: err.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  // Fetch User Reports Success
  FetchUserReportsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.FetchUserReportsSuccessAction),
      switchMap((props) => {
        // Don't perform any further actions
        return of(EmptyAction());
      }),
    ),
  );

  // Fetch User Reports Failure
  FetchUserReportsFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.FetchUserReportsFailureAction),
      switchMap((props) => {
        this.snackBarService.showSnackBar(
          'error',
          'Failed to fetch user reports.',
        );
        return of(EmptyAction());
      }),
    ),
  );
}
